import { Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { useAuthStore } from "./stores/authStore";
import { useFamilyStore } from "./stores/familyStore";
import { useActivityTimeout } from "./hooks/useActivityTimeout";
import RolePicker     from "./screens/RolePicker";
import ParentLogin    from "./screens/ParentLogin";
import ParentHome     from "./screens/ParentHome";
import HouseBuilder   from "./screens/HouseBuilder";
import GiftStars      from "./screens/GiftStars";
import BankSettings   from "./screens/BankSettings";
import KidHome        from "./screens/KidHome";
import Cashout        from "./screens/Cashout";
import Codex          from "./screens/Codex";
import KidStore       from "./screens/KidStore";
import KidWishlist    from "./screens/KidWishlist";
import Setup          from "./screens/Setup";
import Quests         from "./screens/Quests";
import Explorers      from "./screens/Explorers";
import HideStar       from "./screens/HideStar";
import Store          from "./screens/Store";
import StarBank       from "./screens/StarBank";
import Tasks          from "./screens/Tasks";
import JoinFamily     from "./screens/JoinFamily";
import Landing        from "./screens/Landing";
import ThemePicker    from "./screens/ThemePicker";
import KidLogin       from "./screens/KidLogin";

function RequireAuth({ children }: { children: React.ReactNode }) {
  const token = useAuthStore((s) => s.token);
  if (!token) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

// Logs out any active session (parent or kid) after 30 minutes of inactivity
function SessionGuard({ children }: { children: React.ReactNode }) {
  const { token, clearAuth } = useAuthStore();
  const { selectedKid, selectKid } = useFamilyStore();
  const navigate = useNavigate();

  useActivityTimeout(() => {
    const hasKidSession = !!localStorage.getItem("sq_kid_id");
    if (!token && !hasKidSession && !selectedKid) return; // nothing active
    clearAuth();
    selectKid(null);
    navigate("/", { replace: true });
  });

  return <>{children}</>;
}

export default function App() {
  return (
    <SessionGuard>
    <Routes>
      <Route path="/"           element={<Landing />} />
      <Route path="/pick-role"  element={<RolePicker />} />
      <Route path="/login"      element={<ParentLogin />} />
      <Route path="/join/:inviteCode" element={<JoinFamily />} />
      <Route path="/setup"      element={<Setup />} />
      <Route path="/kid/login"  element={<KidLogin />} />
      <Route path="/kid"        element={<KidHome />} />
      <Route path="/kid/cashout" element={<Cashout />} />
      <Route path="/kid/quest-log"  element={<Codex />} />
      <Route path="/kid/store"  element={<KidStore />} />
      <Route path="/kid/store/wishlist" element={<KidWishlist />} />
      <Route path="/kid/theme" element={<ThemePicker />} />
      <Route path="/parent" element={<RequireAuth><ParentHome /></RequireAuth>} />
      <Route path="/parent/builder" element={<RequireAuth><HouseBuilder /></RequireAuth>} />
      <Route path="/parent/gift-points"   element={<RequireAuth><GiftStars /></RequireAuth>} />
      <Route path="/parent/points-value" element={<RequireAuth><BankSettings /></RequireAuth>} />
      <Route path="/parent/challenges"   element={<RequireAuth><Quests /></RequireAuth>} />
      <Route path="/parent/family-hub"  element={<RequireAuth><Explorers /></RequireAuth>} />
      <Route path="/parent/add-challenge"  element={<RequireAuth><HideStar /></RequireAuth>} />
      <Route path="/parent/store"      element={<RequireAuth><Store /></RequireAuth>} />
      <Route path="/parent/points-bank" element={<RequireAuth><StarBank /></RequireAuth>} />
      <Route path="/parent/tasks"      element={<RequireAuth><Tasks /></RequireAuth>} />
      <Route path="*"           element={<Navigate to="/" replace />} />
    </Routes>
    </SessionGuard>
  );
}
